import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  BellOff,
  CarFront,
  Check,
  ChevronsRight,
  CircleAlert,
  Clock,
  Eye,
  Pin,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { ACTIONS, RouteStop } from "@/reducers/routes2gSlice"
import { HoverPopover } from "@/components/ui/hover-popover"
import { isMobile } from "@/utils/detectDevice"
import React from "react"
import { useTranslation } from "react-i18next"
import { Badge } from "@/components/ui/badge"
import { MessageStatus } from "@/lib/messageHelpers"
import { CircularAlertFillIcon } from "@/components/icons/CircularAlertFillIcon"
import DriverRoomIssueIndicator from "@/components/DriverRoomIssueIndicator"
import StationaryIndicator from "@/components/StationaryIndicator"

export interface ChatCardProps {
  name: string
  avatarName: string | undefined
  avatarPhoto: string | undefined
  avatarColor: string

  lastEventText?: string | undefined
  lastEventAuthorName?: string
  lastEventRelativeTime?: string
  lastEventStatus?: MessageStatus | undefined
  isLastEventToDriver?: boolean

  hasRoute?: boolean
  hasGPS?: boolean
  temperatureMain?: number | undefined
  temperatureAdditional?: number | undefined
  temperatureMainVariant?: "neutral" | "positive" | "error" | undefined
  temperatureAdditionalVariant?: "neutral" | "positive" | "error" | undefined
  nextStop?: RouteStop
  stationarySince?: string

  distance?: string
  popoverContent?: React.ReactNode

  isDriving?: boolean
  isLate?: boolean
  isNextStopLate?: boolean
  isMuted?: boolean
  isTracked?: boolean
  isOffRoute?: boolean
  showRouteStatusBadge?: boolean
  showAlert?: boolean
  additionalCount?: number

  unreadCount?: number
  hasPinnedMessages?: boolean

  onClick?: () => void

  hasSyncFailed?: boolean
  isArchived?: boolean
  isArchive?: boolean
  isActive?: boolean
  compact?: boolean

  hasConfigIssues?: boolean
  configIssues?: import("@/hooks/useDriverRoomIssues").DriverRoomIssues | null

  dataTestId?: string
}

const getActionIcon = (stop: RouteStop) => {
  const action =
    ACTIONS.find((action) => action.value.toLowerCase() === stop.action) ||
    ACTIONS.find((action) => action.value === "other")!

  return <action.icon className="h-3 w-3" size={12} />
}

const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

const EventToDriverStatus = ({ status }: { status: MessageStatus }) => {
  switch (status) {
    case "scheduled":
      return <Clock className="size-3.5" />
    case "sent":
      return <Check className="size-3.5" />
    case "received":
      return <CarFront className="size-3.5" />
    case "displayed":
      return <CarFront className="size-3.5 text-blue-500" />
    case "not_sent":
      return <CircularAlertFillIcon className="size-3.5 text-destructive" />
    default:
      return null
  }
}

function ChatCard({
  onClick,
  name,
  avatarName,
  avatarPhoto,
  avatarColor,
  lastEventText,
  lastEventAuthorName,
  lastEventRelativeTime,
  lastEventStatus,
  isLastEventToDriver = false,
  distance,
  popoverContent,
  unreadCount = 0,
  additionalCount,
  isMuted = false,
  isDriving = false,
  isLate = false,
  isNextStopLate = false,
  hasRoute = false,
  hasGPS,
  temperatureMain,
  temperatureAdditional,
  temperatureMainVariant,
  temperatureAdditionalVariant,
  isTracked = false,
  isOffRoute = false,
  showRouteStatusBadge,
  showAlert = false,
  nextStop,
  stationarySince,
  isArchived = false,
  isArchive = false,
  isActive = false,
  hasSyncFailed = false,
  compact = false,
  dataTestId,
  hasPinnedMessages = false,
  hasConfigIssues = false,
  configIssues = null,
}: ChatCardProps) {
  const { t } = useTranslation()
  const shouldShowRouteStatusBadge = showRouteStatusBadge ?? isTracked

  if (compact) {
    return (
      <div
        data-testid={dataTestId}
        className={cn(
          "flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 dark:text-gray-100 dark:hover:bg-gray-800",
          isActive && "bg-sidebar-accent dark:bg-gray-800",
          hasSyncFailed && "opacity-50",
        )}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onClick?.()
        }}
      >
        <Avatar className="h-8 w-8">
          {avatarPhoto && <AvatarImage src={avatarPhoto} alt={name} />}
          <AvatarFallback
            style={{ backgroundColor: avatarColor }}
            className="text-sm font-medium text-gray-900"
          >
            {getInitials(avatarName ?? name)}
          </AvatarFallback>
        </Avatar>
        <span className="truncate font-semibold text-gray-900 dark:text-gray-100">
          {name}
        </span>
      </div>
    )
  }

  return (
    <HoverPopover
      disabled={isMobile || !popoverContent || !hasGPS}
      content={popoverContent}
      side="right"
    >
      <div
        data-testid={dataTestId}
        className={cn(
          "mx-2 flex max-h-[84px] min-h-[84px] min-w-[360px] cursor-pointer items-center gap-2 px-2 py-3",
          isActive
            ? "rounded-md bg-sidebar-accent dark:bg-gray-800"
            : "hover:rounded-md hover:bg-gray-100 dark:hover:bg-gray-800",
          hasSyncFailed && "opacity-50",
        )}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onClick?.()
        }}
      >
        {/* Avatar */}
        <div className="relative self-start">
          <Avatar className={cn(isDriving && "border-2 border-green-600")}>
            {avatarPhoto && <AvatarImage src={avatarPhoto} alt={name} />}
            <AvatarFallback
              style={{ backgroundColor: avatarColor }}
              className="font-medium text-gray-900"
            >
              {getInitials(avatarName ?? name)}
            </AvatarFallback>
          </Avatar>
          {isDriving && (
            <div className="absolute -bottom-1 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-green-600">
              <ChevronsRight
                className="text-white dark:text-primary-foreground"
                size={14}
              />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="text-md mb-0.5 flex min-w-0 flex-1 flex-col self-start">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="truncate font-semibold text-gray-900 dark:text-gray-100">
              {name}
            </h3>
            <div className="flex items-center">
              {unreadCount > 0 && (
                <div className="flex h-5 min-w-5 items-center justify-center rounded-lg bg-base-primary p-1 font-semibold text-white dark:bg-blue-500 dark:text-black">
                  {unreadCount}
                </div>
              )}
            </div>
          </div>

          {/* Message */}
          <div className="mb-0.5 flex items-center justify-between">
            <div
              className={cn(
                "flex min-w-0 items-center gap-1",
                unreadCount > 0
                  ? "font-medium text-accent-foreground"
                  : "text-muted-foreground",
              )}
            >
              {isLastEventToDriver && lastEventStatus ? (
                <EventToDriverStatus status={lastEventStatus} />
              ) : null}
              <p
                className={cn(
                  "min-w-0 flex-1 truncate pr-1",
                  unreadCount > 0
                    ? "font-medium text-accent-foreground"
                    : "text-muted-foreground",
                )}
              >
                {lastEventAuthorName && (
                  <span
                    className={cn(
                      unreadCount > 0
                        ? "text-base-foreground font-medium"
                        : "text-muted-foreground",
                    )}
                  >
                    {lastEventAuthorName}:{" "}
                  </span>
                )}
                {lastEventText}
              </p>{" "}
            </div>

            <span
              className={cn(
                "flex-shrink-0",
                unreadCount > 0
                  ? "font-medium text-base-primary"
                  : "text-muted-foreground",
              )}
            >
              {lastEventRelativeTime}
            </span>
          </div>
          {/* Status and Distance */}

          {((hasRoute && nextStop) ||
            shouldShowRouteStatusBadge ||
            isMuted ||
            isArchived ||
            hasPinnedMessages ||
            !!temperatureMain ||
            !!temperatureAdditional ||
            hasConfigIssues) && (
            <div data-testid="badges" className="flex items-center gap-2">
              {hasConfigIssues && configIssues && (
                <DriverRoomIssueIndicator issues={configIssues} size="sm" />
              )}
              {isArchived && !isArchive && (
                <Badge variant="archived">{t("chat.chatCard.archived")}</Badge>
              )}

              {hasPinnedMessages && (
                <Badge variant="info">
                  <Pin className="h-3 w-3" />
                </Badge>
              )}

              {hasGPS && distance && nextStop && (
                <Badge
                  variant={
                    showAlert
                      ? isNextStopLate
                        ? "error"
                        : "positive"
                      : "neutral"
                  }
                >
                  {getActionIcon(nextStop)}
                  {distance}
                </Badge>
              )}
              {hasGPS && distance && nextStop && (
                <StationaryIndicator
                  proximityStatus={nextStop.proximity_status}
                  stationarySince={stationarySince}
                />
              )}
              {hasRoute && nextStop && isLate && !isNextStopLate && (
                <Badge variant={"error"} title={t("chat.chatCard.lateTitle")}>
                  <CircleAlert className="h-3 w-3" />
                  {t("chat.chatCard.late")}
                </Badge>
              )}

              {shouldShowRouteStatusBadge && (
                <Badge variant={isOffRoute ? "error" : "positive"}>
                  {isOffRoute ? (
                    <>
                      <CircleAlert className="h-3 w-3" />
                      {t("chat.chatCard.offRoute")}
                    </>
                  ) : (
                    <>
                      <Eye className="h-3 w-3" />
                      {t("chat.chatCard.onRoute")}
                    </>
                  )}
                </Badge>
              )}
              {!!temperatureMain && (
                <Badge variant={temperatureMainVariant || "neutral"}>
                  T1: {temperatureMain}°C
                </Badge>
              )}
              {!!temperatureAdditional && (
                <Badge variant={temperatureAdditionalVariant || "neutral"}>
                  T2: {temperatureAdditional}°C
                </Badge>
              )}
              {isMuted && <BellOff className="h-4 w-4 text-gray-400" />}

              {additionalCount && (
                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 dark:bg-gray-800">
                  +{additionalCount}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </HoverPopover>
  )
}

export { ChatCard }
